import React, { useState, useEffect, useRef } from 'react';
import { Product } from '../../types';

interface ProductSelectorProps {
  selectedProductIds: string[];
  onChange: (productIds: string[]) => void;
  products: Product[];
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ selectedProductIds, onChange, products }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on checkbox, label, or inside the dropdown
      if (
        (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') ||
        target.tagName === 'LABEL' ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return;
      }
      // Close if clicking outside
      setIsOpen(false);
      setSearchQuery('');
    };

    if (isOpen) {
      // Use mousedown with a small delay to allow React to process events first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleToggleProduct = (productId: string) => {
    if (selectedProductIds.includes(productId)) {
      onChange(selectedProductIds.filter(id => id !== productId));
    } else {
      onChange([...selectedProductIds, productId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedProductIds.length === filteredProducts.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all filtered products
      onChange(filteredProducts.map(p => p.id));
    }
  };

  const getInitials = (product: Product): string => {
    if (product.projectId) {
      return product.projectId.toUpperCase();
    }
    const name = product.name.trim();
    const words = name.split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (name.length >= 2) {
      return (name[0] + name[1]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getProductColor = (name: string): string => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
      '#6366F1', '#EF4444', '#14B8A6', '#F97316',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const filteredProducts = (products || [])
    .filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const allFilteredSelected = filteredProducts.length > 0 && 
    filteredProducts.every(p => selectedProductIds.includes(p.id));

  const displayText = selectedProductIds.length === 0
    ? 'All products'
    : selectedProductIds.length === 1
    ? products.find(p => p.id === selectedProductIds[0])?.name || '1 product'
    : `${selectedProductIds.length} products`;

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '0.5rem',
          fontSize: '0.875rem',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid #ddd',
          borderRadius: '4px',
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--cyan-glow)';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = '#ddd';
          }
        }}
      >
        <span>{displayText}</span>
        <span style={{ 
          fontSize: '0.75rem', 
          color: 'var(--cyan-glow)',
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '0.5rem',
            background: 'var(--surface)',
            border: '1px solid var(--grid-line)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '300px',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--grid-line)' }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Type to search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '0.5rem 2rem 0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid var(--grid-line)',
                  borderRadius: '4px',
                  background: 'var(--surface-alt)',
                  color: 'var(--text-primary)',
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery('');
                    searchInputRef.current?.focus();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Select All option */}
          {filteredProducts.length > 0 && (
            <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--grid-line)' }}>
              <label
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'background-color 0.15s',
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--surface-elevated)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    cursor: 'pointer',
                    accentColor: 'var(--cyan-glow)',
                    width: '16px',
                    height: '16px',
                    margin: 0,
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem',
                  userSelect: 'none',
                }}>
                  Select All
                </span>
              </label>
            </div>
          )}

          {/* Product list */}
          <div style={{
            padding: '0.25rem',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            maxHeight: '200px',
          }}>
            {products.length === 0 ? (
              <div style={{
                padding: '1rem',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                textAlign: 'center',
              }}>
                No products available
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{
                padding: '1rem',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                textAlign: 'center',
              }}>
                No products found
              </div>
            ) : (
              filteredProducts.map(product => {
                const isSelected = selectedProductIds.includes(product.id);
                const initials = getInitials(product);
                const color = getProductColor(product.name);
                return (
                  <label
                    key={product.id}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.625rem 0.75rem',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.15s',
                      backgroundColor: isSelected ? 'var(--cyan-transparent-light)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isSelected
                        ? 'var(--cyan-transparent-light)'
                        : 'var(--surface-elevated)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isSelected
                        ? 'var(--cyan-transparent-light)'
                        : 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleProduct(product.id)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        cursor: 'pointer',
                        accentColor: 'var(--cyan-glow)',
                        width: '16px',
                        height: '16px',
                        margin: 0,
                        flexShrink: 0,
                      }}
                    />
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        background: color,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    <span style={{
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem',
                      userSelect: 'none',
                      flex: 1,
                    }}>
                      {product.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;

